import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Eye,
  ChevronDown,
  ChevronRight,
  ShoppingBag,
  Loader2,
  Copy,
  Scissors,
  Plus,
  FileText,
  AlertTriangle,
  Send,
  Tag,
  Printer,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { ConfirmDialog } from '@components/Dialog';
import { PendingOrdersModal } from '@components/PendingOrdersModal';
import { DateInputBR } from '@components/DateInputBR';
import { salesOrderService } from '../../services/salesOrderService';
import { productService } from '../../services/productService';
import { companyService } from '../../services/companyService';
import { orderTypeService } from '../../services/orderTypeService';
import { Company, formatCnpj } from '../../types/company';
import { OrderType } from '../../types/orderType';
import {
  SalesOrder,
  SalesOrderItem,
  OrderStatusOption,
  SalesOrderSearchModel,
  SALES_ORDER_STATUS_OPTIONS,
  SALES_ORDER_MANUAL_STATUS_OPTIONS,
  toManualStatusOptions,
  OrderStatusId,
  getStatusOptionById,
} from '../../types/salesOrder';
import { SplitOrderModal } from './SplitOrderModal';
import {
  BATCH_PDF_MAX,
  chunkIds,
  pageSelectionState,
  toggleOrderSelection,
  togglePageSelection,
} from './batchPrint';
import { usePermissions } from '@/hooks/usePermissions';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (value?: number) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

const formatCNPJ = (cnpj?: string) => {
  if (!cnpj) return '—';
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
};

// Status badge colors per user specification
const getStockBadge = (stockUpdated?: boolean | null, orderStatusId?: number | null, allocateAvailableStock?: boolean) => {
  if (stockUpdated === true) {
    if (orderStatusId === OrderStatusId.Cancelado) {
      return (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
          title="Estoque físico foi lançado e depois estornado"
        >
          Estoque estornado
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700"
        title="Estoque físico já foi atualizado"
      >
        Estoque lançado
      </span>
    );
  }
  // stock_updated is false or null
  if (orderStatusId === OrderStatusId.Cancelado) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500"
        title="Pedido cancelado — sem reserva de estoque"
      >
        —
      </span>
    );
  }
  if (!allocateAvailableStock) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500"
        title="Status atual não reserva estoque"
      >
        —
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
      title="Itens do pedido estão alocando estoque virtual"
    >
      Reservando estoque
    </span>
  );
};

const STATUS_BADGE_CLASSES: Record<number, string> = {
  [OrderStatusId.EmAberto]:       'bg-yellow-100 text-yellow-800',   // amarelo claro
  [OrderStatusId.EmDigitacao]:    'bg-blue-100 text-blue-700',       // azul claro
  [OrderStatusId.EmAndamento]:    'bg-blue-100 text-blue-700',       // azul claro
  [OrderStatusId.Atendido]:       'bg-green-100 text-green-700',     // verde claro
  [OrderStatusId.EmConferencia]:  'bg-blue-200 text-blue-800',       // azul um pouco mais escuro
  [OrderStatusId.Cancelado]:      'bg-red-100 text-red-700',         // vermelho
  [OrderStatusId.Conferido]:      'bg-blue-300 text-blue-900',       // azul intermediário
  [OrderStatusId.VendaAgenciada]: 'bg-amber-100 text-amber-700',     // entre amarelo claro e laranja
  [OrderStatusId.Enviado]:        'bg-indigo-100 text-indigo-700',   // indigo
  [OrderStatusId.Entregue]:       'bg-teal-100 text-teal-700',       // teal para diferenciar de Atendido
  [OrderStatusId.DesmembradoPai]: 'bg-gray-100 text-gray-700',   // cinza claro
};

const getStatusBadge = (statusId?: number) => {
  if (!statusId) return <span className="text-slate-500">—</span>;
  const opt = getStatusOptionById(statusId);
  const label = opt?.label ?? `Status ${statusId}`;
  const cls = STATUS_BADGE_CLASSES[statusId] ?? 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
};

// Default date range: last 3 months
const getDefault3MonthsAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().split('T')[0];
};
const getToday = () => new Date().toISOString().split('T')[0];

const STORAGE_KEY = 'sales-orders-list-state';

const readSaved = () => {
  try {
    const s = sessionStorage.getItem(STORAGE_KEY);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
};

// Returns the page numbers (and ellipsis) to display around the current page
const getPageRange = (current: number, total: number): (number | '...')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [1];
  if (current > 3) pages.push('...');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p);
  }
  if (current < total - 2) pages.push('...');
  pages.push(total);
  return pages;
};

// ─── Component ───────────────────────────────────────────────────

export const SalesOrderListView: React.FC = () => {
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();
  // Admin e Finance podem reabrir o status de um pedido cancelado (mesmo override do detalhe).
  const canOverrideBlockOrderChange =
    checkPermission('orders.mark_paid').hasPermission || checkPermission('*').hasPermission;

  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState<string>(() => readSaved()?.customerSearch ?? '');
  // Debounced value that actually triggers API calls (avoids React StrictMode double-effect issues)
  const [searchTerm, setSearchTerm] = useState<string>(() => readSaved()?.customerSearch ?? '');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{
    company_id: number | undefined;
    order_status_id: number | undefined;
    order_type_id?: number | undefined;
    date_from: string;
    date_to: string;
    stock_filter: string | undefined;
  }>(() => readSaved()?.filters ?? {
    company_id: undefined,
    order_status_id: undefined,
    order_type_id: undefined,
    date_from: getDefault3MonthsAgo(),
    date_to: getToday(),
    stock_filter: undefined,
  });

  // Lookups
  const [companies, setCompanies] = useState<Company[]>([]);
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(() => readSaved()?.currentPage ?? 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalAmount, setTotalAmount] = useState<number | null>(null);
  const pageSize = 20;

  // Expanded items toggle + per-order items cache (list API does not include items)
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [orderItemsCache, setOrderItemsCache] = useState<Record<number, { loading: boolean; items: SalesOrderItem[] }>>({});

  // Stock data cache per SKU (loaded when order items expand)
  const [stockCache, setStockCache] = useState<Record<string, { product_id: number; stock_quantity: number | null; stock_quantity_available: number | null; min_stock_quantity: number | null }>>({});

  // Pending orders modal
  const [pendingModal, setPendingModal] = useState<{ open: boolean; productId: number | null; sku: string; description?: string }>({ open: false, productId: null, sku: '' });

  // Inline status edit
  const [editingStatusOrderId, setEditingStatusOrderId] = useState<number | null>(null);

  // Clone
  const [cloneOrderId, setCloneOrderId] = useState<number | null>(null);
  const [cloning, setCloning] = useState(false);

  // Split
  const [splitOrder, setSplitOrder] = useState<SalesOrder | null>(null);
  const [splitting, setSplitting] = useState(false);

  // Status change
  const [statusChange, setStatusChange] = useState<{ orderId: number; statusId: number } | null>(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const [allStatuses, setAllStatuses] = useState<OrderStatusOption[]>([]);
  const [manualStatusOpts, setManualStatusOpts] = useState(() =>
    toManualStatusOptions(SALES_ORDER_MANUAL_STATUS_OPTIONS.map(s => ({
      order_status_id: s.id,
      status_name: s.value,
      can_user_choose_manually: true,
      block_order_change: false,
      allocate_available_stock: false,
    }))),
  );
  // Quick navigation
  const [quickNavValue, setQuickNavValue] = useState('');
  const [quickNavType, setQuickNavType] = useState<'erp' | 'ecommerce' | 'id'>('id');
  const [quickNavLoading, setQuickNavLoading] = useState(false);
  const [quickNavError, setQuickNavError] = useState<string | null>(null);

  // Batch PDF: pedidos marcados para impressão em massa (sobrevivem à troca de página)
  const [batchPdfLoading, setBatchPdfLoading] = useState<'list' | 'selected' | null>(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);

  // Imprime os pedidos informados (a listagem da página ou os marcados) em PDFs de até 50
  // pedidos, cada um aberto numa aba nova pronto para imprimir.
  const handleBatchPrint = async (ids: number[], source: 'list' | 'selected') => {
    const batches = chunkIds(ids, BATCH_PDF_MAX);
    if (batches.length === 0) return;
    // Abas abertas no clique (antes de qualquer await) para o navegador não bloquear; o
    // navegador pode liberar só a primeira — os lotes sem aba são baixados.
    const tabs = batches.map(() => window.open('', '_blank'));
    tabs.forEach(t => {
      try { t?.document.write('<p style="font-family:sans-serif;padding:24px">Gerando o PDF dos pedidos...</p>'); } catch { /* aba sem acesso */ }
    });
    setBatchPdfLoading(source);
    setError(null);
    try {
      for (const [i, batch] of batches.entries()) {
        await salesOrderService.downloadBatchPdf(batch, tabs[i], batches.length > 1 ? `-parte-${i + 1}` : '');
      }
      if (source === 'selected') setSelectedOrderIds([]);
    } catch (err) {
      tabs.forEach(t => { if (t && !t.closed) t.close(); });
      setError(err instanceof Error ? err.message : 'Erro ao gerar PDF em lote');
    } finally {
      setBatchPdfLoading(null);
    }
  };
  // ── Load ──────────────────────────────────────────────────────

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const searchFilters: SalesOrderSearchModel = {
        page: currentPage,
        pageSize,
        company_id: filters.company_id,
        order_status_id: filters.order_status_id,
        order_type_id: filters.order_type_id,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        customer_name: searchTerm.trim() || undefined,
      };

      const response = await salesOrderService.getOrders(searchFilters);
      const data = Array.isArray(response.data) ? response.data : [];
      setOrders(data);
      setTotalOrders(response.pagination?.total || data.length);
      setTotalAmount(response.summary?.total_amount_sum ?? null);
      setTotalPages(response.pagination?.totalPages || 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, searchTerm]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Persist filter state to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ filters, customerSearch, currentPage }));
    } catch {
      // Ignorado de propósito: guardar o filtro é conveniência. Em aba anônima
      // ou com a cota estourada, o sessionStorage lança — e perder o filtro não
      // pode quebrar a listagem.
    }
  }, [filters, customerSearch, currentPage]);

  useEffect(() => {
    companyService.getCompanies().then(setCompanies).catch(() => {});
    orderTypeService.listAll().then(setOrderTypes).catch(() => {});
    salesOrderService.getOrderStatuses().then(data => {
      setAllStatuses(data);
      const opts = toManualStatusOptions(data);
      if (opts.length > 0) setManualStatusOpts(opts);
    }).catch(() => {});
  }, []);

  const isOrderStatusBlocked = (statusId?: number) => {
    const fromApi = allStatuses.find(s => s.order_status_id === statusId);
    if (fromApi) return fromApi.block_order_change;
    return [
      OrderStatusId.Atendido,
      OrderStatusId.Cancelado,
      OrderStatusId.Enviado,
      OrderStatusId.Entregue,
    ].includes(statusId ?? 0);
  };

  // Debounce search: update input immediately, commit searchTerm + reset page after delay
  const handleSearchChange = (value: string) => {
    setCustomerSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      setSearchTerm(value);
    }, 500);
  };

  // Filter dropdown outside-click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown') && !target.closest('[data-filter-trigger]')) {
        setShowFilters(false);
      }
    };
    if (showFilters) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  // ── Clone ─────────────────────────────────────────────────────

  const handleClone = async () => {
    if (cloneOrderId === null) return;
    try {
      setCloning(true);
      // Fetch full order (with items, installments, etc.) to pre-fill the form
      const fullOrder = await salesOrderService.getOrderById(cloneOrderId);
      setCloneOrderId(null);
      navigate('/sales-orders/new', { state: { cloneSource: fullOrder } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar pedido para clonagem');
    } finally {
      setCloning(false);
    }
  };

  // ── Split ─────────────────────────────────────────────────────

  const handleSplitConfirm = async (
    order1StatusId: number,
    order2StatusId: number,
    order1Items: { sku: string; quantity: number; unit_price?: number; unit_cost?: number; discount?: number }[],
    order2Items: { sku: string; quantity: number; unit_price?: number; unit_cost?: number; discount?: number }[],
    replacementItemsTarget: 1 | 2 | null,
  ) => {
    if (!splitOrder) return;
    const isStatusBlocked = isOrderStatusBlocked(splitOrder.order_status_id);
    if (isStatusBlocked) {
      setError('Este pedido não pode ser desmembrado porque o status atual bloqueia alterações.');
      setSplitOrder(null);
      return;
    }
    try {
      setSplitting(true);
      await salesOrderService.splitOrder({
        parent_order_id: splitOrder.parent_order_id ?? splitOrder.order_id,
        source_order_id: splitOrder.parent_order_id ? splitOrder.order_id : undefined,
        order1_status_id: order1StatusId,
        order2_status_id: order2StatusId,
        order1_items: order1Items,
        order2_items: order2Items,
        replacement_items_target_order: replacementItemsTarget ?? undefined,
        is_resplit: Boolean(splitOrder.parent_order_id),
      });
      setSplitOrder(null);
      loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao desmembrar pedido');
    } finally {
      setSplitting(false);
    }
  };

  // Before opening split, we need full order data with items
  const handleOpenSplit = async (orderId: number) => {
    try {
      const fullOrder = await salesOrderService.getOrderById(orderId);
      const isStatusBlocked = isOrderStatusBlocked(fullOrder.order_status_id);
      if (isStatusBlocked) {
        setError('Este pedido não pode ser desmembrado porque o status atual bloqueia alterações.');
        return;
      }
      setSplitOrder(fullOrder);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar pedido para desmembramento');
    }
  };

  // ── Status change ─────────────────────────────────────────────

  const handleStatusChange = async (orderId: number, newStatusId: number) => {
    setStatusChange({ orderId, statusId: newStatusId });
  };

  const confirmStatusChange = async () => {
    if (!statusChange) return;
    try {
      setChangingStatus(true);
      await salesOrderService.updateOrderStatus(statusChange.orderId, statusChange.statusId);
      setStatusChange(null);
      loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao alterar status');
    } finally {
      setChangingStatus(false);
    }
  };

  // ── Active filter count ───────────────────────────────────────

  const activeFilterCount = [
    filters.company_id !== undefined,
    filters.order_status_id !== undefined,
    filters.order_type_id !== undefined,
    filters.stock_filter !== undefined,
    filters.date_from !== getDefault3MonthsAgo(),
    filters.date_to !== getToday(),
  ].filter(Boolean).length;

  // ── Toggle expand (lazy-loads items via getOrderById) ──────────

  const toggleExpand = async (orderId: number) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
      return;
    }
    setExpandedOrderId(orderId);
    if (!orderItemsCache[orderId]) {
      setOrderItemsCache(prev => ({ ...prev, [orderId]: { loading: true, items: [] } }));
      try {
        const fullOrder = await salesOrderService.getOrderById(orderId);
        const items = fullOrder.items || [];
        setOrderItemsCache(prev => ({
          ...prev,
          [orderId]: { loading: false, items },
        }));
        // Fetch stock data for item SKUs not yet cached
        const skusToFetch = items.map(i => i.sku).filter(sku => sku && !stockCache[sku]);
        if (skusToFetch.length > 0) {
          productService.getStockBySku(skusToFetch).then(stockData => {
            setStockCache(prev => ({ ...prev, ...stockData }));
          }).catch(() => { /* ignore stock lookup errors */ });
        }
      } catch {
        setOrderItemsCache(prev => ({ ...prev, [orderId]: { loading: false, items: [] } }));
      }
    }
  };

  // ── Quick Navigation ───────────────────────────────────────

  const handleQuickNav = async () => {
    const q = quickNavValue.trim();
    if (!q) return;
    try {
      setQuickNavLoading(true);
      setQuickNavError(null);
      const orderId = await salesOrderService.lookupOrder(q, quickNavType);
      navigate(`/sales-orders/${orderId}`);
    } catch (err) {
      setQuickNavError(err instanceof Error ? err.message : 'Pedido não encontrado');
    } finally {
      setQuickNavLoading(false);
    }
  };

  const QUICK_NAV_OPTIONS: { value: 'erp' | 'ecommerce' | 'id'; label: string; placeholder: string }[] = [
    { value: 'id',        label: 'ID Interno',   placeholder: 'ID interno...' },
    { value: 'erp',       label: 'Nº ERP',       placeholder: 'Número ERP...' },
    { value: 'ecommerce', label: 'Ecommerce/CRM', placeholder: 'Nº ecommerce ou CRM...' },
  ];

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pedidos de Venda</h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalOrders} pedido{totalOrders !== 1 ? 's' : ''} encontrado{totalOrders !== 1 ? 's' : ''}
            {totalAmount != null && (
              <>
                {' · '}
                <span
                  className="font-semibold text-slate-700"
                  title="Soma do valor dos pedidos de todo o filtro (todas as páginas). Não inclui pedidos desmembrados filhos, demonstração, presente nem cancelados."
                >
                  Valor total: {formatCurrency(totalAmount)}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex flex-col items-stretch sm:items-end gap-2">
          <div className="flex items-center gap-2">
            {/* Quick navigation — hidden on mobile, shown inline on sm+ */}
            <div className="hidden sm:flex items-center bg-white border border-slate-200 rounded-lg shadow-sm h-9 overflow-hidden">
              <select
                className="bg-slate-50 border-r border-slate-200 text-xs text-slate-600 px-2 h-full outline-none cursor-pointer hover:bg-slate-100"
                value={quickNavType}
                onChange={e => { setQuickNavType(e.target.value as 'erp' | 'ecommerce' | 'id'); setQuickNavError(null); }}
                title="Tipo de busca"
              >
                {QUICK_NAV_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input
                type="text"
                className="bg-transparent text-sm outline-none px-2.5 w-40 placeholder:text-slate-400"
                placeholder={QUICK_NAV_OPTIONS.find(o => o.value === quickNavType)?.placeholder}
                value={quickNavValue}
                onChange={e => { setQuickNavValue(e.target.value); setQuickNavError(null); }}
                onKeyDown={e => e.key === 'Enter' && handleQuickNav()}
              />
              <button
                className="text-slate-500 hover:text-emerald-600 disabled:opacity-40 flex items-center px-2 h-full border-l border-slate-200 hover:bg-slate-50"
                onClick={handleQuickNav}
                disabled={quickNavLoading || !quickNavValue.trim()}
                title="Ir para o pedido"
              >
                {quickNavLoading
                  ? <Loader2 size={15} className="animate-spin" />
                  : <ChevronRight size={15} />}
              </button>
            </div>
            <PermissionGuard permission="orders.create">
              <Button icon={<Plus size={16} />} onClick={() => navigate('/sales-orders/new')}>
                Novo Pedido
              </Button>
            </PermissionGuard>
            <PermissionGuard permission="orders.read">
              <Button
                variant="outline"
                icon={<Printer size={16} />}
                disabled={batchPdfLoading !== null || orders.length === 0}
                onClick={() => handleBatchPrint(orders.map(o => o.order_id), 'list')}
                title="Gera um PDF com todos os pedidos desta página (até 50 por arquivo)"
                className="hidden sm:inline-flex"
              >
                {batchPdfLoading === 'list' ? 'Gerando...' : 'Imprimir listagem'}
              </Button>
              <Button
                variant="secondary"
                icon={<Printer size={16} />}
                disabled={batchPdfLoading !== null || selectedOrderIds.length === 0}
                onClick={() => handleBatchPrint(selectedOrderIds, 'selected')}
                title="Marque os pedidos na lista (ou todos pelo quadrado do cabeçalho), desmarque os que não quiser e imprima só os marcados"
                className="hidden sm:inline-flex"
              >
                {batchPdfLoading === 'selected' ? 'Gerando...' : `Imprimir selecionados (${selectedOrderIds.length})`}
              </Button>
              {selectedOrderIds.length > 0 && !batchPdfLoading && (
                <button
                  type="button"
                  onClick={() => setSelectedOrderIds([])}
                  className="hidden sm:inline text-xs text-slate-500 hover:text-slate-700 underline"
                >
                  Limpar seleção
                </button>
              )}
            </PermissionGuard>
          </div>
          {/* Quick navigation — mobile only, shown below buttons */}
          <div className="sm:hidden">
            <div className="flex items-center bg-white border border-slate-200 rounded-lg shadow-sm h-9 overflow-hidden w-full">
              <select
                className="bg-slate-50 border-r border-slate-200 text-xs text-slate-600 px-2 h-full outline-none cursor-pointer hover:bg-slate-100"
                value={quickNavType}
                onChange={e => setQuickNavType(e.target.value as typeof quickNavType)}
              >
                {QUICK_NAV_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input
                type="text"
                className="bg-transparent text-sm outline-none px-2.5 flex-1 placeholder:text-slate-400"
                placeholder={QUICK_NAV_OPTIONS.find(o => o.value === quickNavType)?.placeholder}
                value={quickNavValue}
                onChange={e => { setQuickNavValue(e.target.value); setQuickNavError(null); }}
                onKeyDown={e => e.key === 'Enter' && handleQuickNav()}
              />
              <button
                className="text-slate-500 hover:text-emerald-600 disabled:opacity-40 flex items-center px-2 h-full border-l border-slate-200 hover:bg-slate-50"
                onClick={handleQuickNav}
                disabled={quickNavLoading || !quickNavValue.trim()}
                title="Ir para o pedido"
              >
                {quickNavLoading
                  ? <Loader2 size={15} className="animate-spin" />
                  : <ChevronRight size={15} />}
              </button>
            </div>
          </div>
          {quickNavError && (
            <p className="text-xs text-red-500">{quickNavError}</p>
          )}
        </div>
      </div>

      {/* Search & filters */}
      <Card padding="none">
        <CardContent className="py-2 px-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                className="input pl-10 w-full"
                placeholder="Buscar por nome do cliente..."
                value={customerSearch}
                onChange={e => handleSearchChange(e.target.value)}
              />
            </div>
            <div className="relative">
              <Button
                variant="outline"
                data-filter-trigger
                icon={<Filter size={18} />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filtros
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 bg-emerald-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown size={16} className="ml-1" />
              </Button>

              {showFilters && (
                <div className="filter-dropdown absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-slate-200 p-4 z-30 space-y-4">
                  {/* Empresa */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Empresa</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.company_id !== undefined ? filters.company_id : ''}
                      onChange={e => {
                        const val = e.target.value;
                        setFilters(prev => ({ ...prev, company_id: val === '' ? undefined : Number(val) }));
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Todas</option>
                      {companies.map(c => (
                        <option key={c.company_id} value={c.company_id}>{c.name} — {formatCnpj(c.document)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.order_status_id ?? ''}
                      onChange={e => {
                        const val = e.target.value ? Number(e.target.value) : undefined;
                        setFilters(prev => ({ ...prev, order_status_id: val }));
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Todos</option>
                      {SALES_ORDER_STATUS_OPTIONS.map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Tipo de pedido */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Tipo de pedido</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.order_type_id ?? ''}
                      onChange={e => {
                        const val = e.target.value ? Number(e.target.value) : undefined;
                        setFilters(prev => ({ ...prev, order_type_id: val }));
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Todos</option>
                      {orderTypes.map(t => (
                        <option key={t.order_type_id} value={t.order_type_id}>{t.order_type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Estoque */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Estoque</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.stock_filter || ''}
                      onChange={e => {
                        const val = e.target.value;
                        setFilters(prev => ({ ...prev, stock_filter: val || undefined }));
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Todos</option>
                      <option value="reserving">Reservando estoque</option>
                      <option value="updated">Estoque lançado</option>
                    </select>
                  </div>

                  {/* Datas */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Data de</label>
                      <DateInputBR
                        className="input w-full text-sm"
                        value={filters.date_from}
                        onChange={v => { setFilters(prev => ({ ...prev, date_from: v })); setCurrentPage(1); }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Data até</label>
                      <DateInputBR
                        className="input w-full text-sm"
                        value={filters.date_to}
                        onChange={v => { setFilters(prev => ({ ...prev, date_to: v })); setCurrentPage(1); }}
                      />
                    </div>
                  </div>

                  <button
                    className="text-xs text-emerald-600 hover:underline"
                    onClick={() => { setFilters({ company_id: undefined, order_status_id: undefined, order_type_id: undefined, stock_filter: undefined, date_from: getDefault3MonthsAgo(), date_to: getToday() }); setCurrentPage(1); }}
                  >
                    Limpar filtros
                  </button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active filters strip */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500 text-xs font-medium">Filtros ativos:</span>
        {/* Date range — always shown */}
        <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-3 py-0.5 text-xs">
          Período: {formatDate(filters.date_from)} — {formatDate(filters.date_to)}
          {(filters.date_from !== getDefault3MonthsAgo() || filters.date_to !== getToday()) && (
            <button
              className="ml-1 text-slate-400 hover:text-slate-600"
              onClick={() => { setFilters(prev => ({ ...prev, date_from: getDefault3MonthsAgo(), date_to: getToday() })); setCurrentPage(1); }}
              title="Resetar período"
            >×</button>
          )}
        </span>
        {filters.order_status_id !== undefined && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-0.5 text-xs">
            Status: {getStatusOptionById(filters.order_status_id)?.label ?? `ID ${filters.order_status_id}`}
            <button className="ml-1 text-emerald-400 hover:text-emerald-600" onClick={() => { setFilters(prev => ({ ...prev, order_status_id: undefined })); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
        {filters.order_type_id !== undefined && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-0.5 text-xs">
            Tipo: {orderTypes.find(t => t.order_type_id === filters.order_type_id)?.order_type ?? `ID ${filters.order_type_id}`}
            <button className="ml-1 text-emerald-400 hover:text-emerald-600" onClick={() => { setFilters(prev => ({ ...prev, order_type_id: undefined })); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
        {filters.stock_filter && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-0.5 text-xs">
            Estoque: {filters.stock_filter === 'reserving' ? 'Reservando estoque' : 'Estoque lançado'}
            <button className="ml-1 text-emerald-400 hover:text-emerald-600" onClick={() => { setFilters(prev => ({ ...prev, stock_filter: undefined })); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
        {filters.company_id !== undefined && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-0.5 text-xs">
            Empresa: {companies.find(c => c.company_id === filters.company_id)?.name ?? filters.company_id}
            <button className="ml-1 text-emerald-400 hover:text-emerald-600" onClick={() => { setFilters(prev => ({ ...prev, company_id: undefined })); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
        {customerSearch.trim() && (
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-3 py-0.5 text-xs">
            Busca: "{customerSearch.trim()}"
            <button className="ml-1 text-emerald-400 hover:text-emerald-600" onClick={() => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); setCustomerSearch(''); setSearchTerm(''); setCurrentPage(1); }} title="Remover">×</button>
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <button className="ml-2 underline" onClick={() => setError(null)}>Fechar</button>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-emerald-600" size={32} />
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <ShoppingBag size={48} className="mb-3" />
              <p className="text-lg font-medium">Nenhum pedido encontrado</p>
              <p className="text-sm mt-1">Ajuste os filtros ou período de busca.</p>
            </div>
          ) : (() => {
            // Apply client-side stock filter
            const filteredOrders = filters.stock_filter
              ? orders.filter(o => {
                  if (filters.stock_filter === 'reserving') {
                    return (!o.stock_updated) && o.order_status_id !== OrderStatusId.Cancelado;
                  }
                  if (filters.stock_filter === 'updated') {
                    return o.stock_updated === true;
                  }
                  return true;
                })
              : orders;
            return filteredOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                <ShoppingBag size={48} className="mb-3" />
                <p className="text-lg font-medium">Nenhum pedido corresponde ao filtro de estoque</p>
                <p className="text-sm mt-1">Ajuste os filtros.</p>
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm border-collapse">
                <TableHeader>
                  <tr>
                    <th className="py-3 pl-3 pr-1 w-8">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 cursor-pointer"
                        title="Marcar/desmarcar todos os pedidos desta página para impressão"
                        aria-label="Marcar todos os pedidos da página para impressão"
                        checked={pageSelectionState(filteredOrders.map(o => o.order_id), selectedOrderIds) === 'all'}
                        ref={el => {
                          if (el) el.indeterminate = pageSelectionState(filteredOrders.map(o => o.order_id), selectedOrderIds) === 'some';
                        }}
                        onChange={() => setSelectedOrderIds(prev => togglePageSelection(filteredOrders.map(o => o.order_id), prev))}
                      />
                    </th>
                    <th className="py-3 px-2 w-8"></th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">ID</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase" title="Data limite de recebimento pelo cliente: prazo oferecido no checkout, em dias úteis, a partir da data do pedido">Data Limite</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Nº E-commerce/CRM</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cliente</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">CNPJ Empresa</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Cidade/UF</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Tipo</th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">1ª Compra</th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">Est.</th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">Ações</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {filteredOrders.map(order => {
                    const currentOrderType = orderTypes.find(t => t.order_type_id === order.order_type_id);
                    const isOrderTypeFullGift = Boolean(
                      currentOrderType?.generate_invoice && !currentOrderType?.has_financial_value,
                    );
                    return (
                    <React.Fragment key={order.order_id}>
                      <TableRow className={selectedOrderIds.includes(order.order_id) ? 'bg-emerald-50/60' : undefined}>
                        <TableCell className="pl-3 pr-1 w-8">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-emerald-600 cursor-pointer"
                            aria-label={`Marcar o pedido ${order.order_id} para impressão`}
                            checked={selectedOrderIds.includes(order.order_id)}
                            onChange={() => setSelectedOrderIds(prev => toggleOrderSelection(prev, order.order_id))}
                          />
                        </TableCell>
                        <TableCell className="px-2 w-8">
                          <button
                            onClick={() => toggleExpand(order.order_id)}
                            className="p-1 rounded hover:bg-slate-200 transition-colors text-slate-400 hover:text-slate-600"
                            title={expandedOrderId === order.order_id ? 'Recolher itens' : 'Expandir itens'}
                          >
                            {expandedOrderId === order.order_id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </TableCell>
                        <TableCell className="font-medium text-slate-900">
                          <button
                            className="text-emerald-700 hover:underline font-semibold"
                            onClick={() => navigate(`/sales-orders/${order.order_id}`)}
                          >
                            {order.order_id}
                          </button>
                          {order.parent_order_id && (
                            <span className="block text-xs text-indigo-500">
                              Filho de #{order.parent_order_id}
                            </span>
                          )}
                          {order.child_orders && order.child_orders.length > 0 && (
                            <span className="block text-xs text-indigo-500">
                              Pai de {order.child_orders.map(c => `#${c.order_id}`).join(', ')}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(order.order_date)}</TableCell>
                        <TableCell>
                          {order.delivery_deadline ? (
                            <div className="flex flex-col gap-0.5">
                              <span className={order.is_delivery_late ? 'text-red-700 font-medium' : 'text-slate-600'}>
                                {formatDate(order.delivery_deadline)}
                              </span>
                              {order.is_delivery_late && (
                                <span className="inline-flex w-fit items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                                  <AlertTriangle size={12} /> Atrasado
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-600">{order.order_num_ecommerce || '—'}</TableCell>
                        <TableCell>
                          <span className="text-slate-900">{order.customer_name || '—'}</span>
                        </TableCell>
                        <TableCell className="text-slate-500 text-xs font-mono">
                          {formatCNPJ(order.company_document)}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {order.ship_addr_city && order.ship_addr_state
                            ? `${order.ship_addr_city}/${order.ship_addr_state}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right font-medium text-slate-800">
                          {formatCurrency(order.order_sell_total)}
                        </TableCell>
                        <TableCell>
                          {(order.order_status_id === OrderStatusId.Cancelado && !canOverrideBlockOrderChange) || order.order_status_id === OrderStatusId.Atendido || !!order.invoice_id ? (
                            getStatusBadge(order.order_status_id)
                          ) : editingStatusOrderId === order.order_id ? (
                            <PermissionGuard permission="orders.change_status" fallback={getStatusBadge(order.order_status_id)}>
                              <select
                                autoFocus
                                className="text-xs border border-slate-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                value={order.order_status_id ?? ''}
                                onChange={e => { handleStatusChange(order.order_id, Number(e.target.value)); setEditingStatusOrderId(null); }}
                                onBlur={() => setEditingStatusOrderId(null)}
                              >
                                {manualStatusOpts.map(s => (
                                  <option key={s.id} value={s.id}>{s.label}</option>
                                ))}
                              </select>
                            </PermissionGuard>
                          ) : (
                            <PermissionGuard permission="orders.change_status" fallback={getStatusBadge(order.order_status_id)}>
                              <button
                                type="button"
                                onClick={() => setEditingStatusOrderId(order.order_id)}
                                title="Clique para alterar o status"
                                className="cursor-pointer hover:opacity-80 transition-opacity"
                              >
                                {getStatusBadge(order.order_status_id)}
                              </button>
                            </PermissionGuard>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-600 whitespace-nowrap">
                          {currentOrderType?.order_type || '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          {order.is_first_purchase ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700" title="Primeira compra do cliente">
                              1ª
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {getStockBadge(order.stock_updated, order.order_status_id, allStatuses.find(s => s.order_status_id === order.order_status_id)?.allocate_available_stock)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <PermissionGuard permission="orders.read">
                              <button
                                className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                title="Visualizar"
                                onClick={() => navigate(`/sales-orders/${order.order_id}`)}
                              >
                                <Eye size={16} />
                              </button>
                            </PermissionGuard>
                            <PermissionGuard permission="orders.clone">
                              <button
                                className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Clonar pedido"
                                onClick={() => setCloneOrderId(order.order_id)}
                              >
                                <Copy size={16} />
                              </button>
                            </PermissionGuard>
                            {!!order.invoice_id && (
                              <PermissionGuard permission="invoices.read">
                                <button
                                  className="p-1.5 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                                  title={`Nota Fiscal${order.invoice_number ? ` #${order.invoice_number}` : ` (ID: ${order.invoice_id})`}`}
                                  onClick={() => navigate(`/invoices/${order.invoice_id}`)}
                                >
                                  <FileText size={16} />
                                </button>
                              </PermissionGuard>
                            )}
                            {!!order.gift_invoice_id && (
                              <PermissionGuard permission="invoices.read">
                                <button
                                  className="p-1.5 text-pink-600 hover:text-pink-800 hover:bg-pink-50 rounded transition-colors"
                                  title={`NF Bonificação${order.gift_invoice_number ? ` #${order.gift_invoice_number}` : ` (ID: ${order.gift_invoice_id})`}`}
                                  onClick={() => navigate(`/invoices/${order.gift_invoice_id}`)}
                                >
                                  <FileText size={16} />
                                </button>
                              </PermissionGuard>
                            )}
                            {!order.invoice_id && (
                              [OrderStatusId.Conferido, OrderStatusId.Atendido].includes(order.order_status_id ?? 0)
                              || (!!order.gift_invoice_id && !order.invoice_id)
                            ) && order.order_status_id !== OrderStatusId.Cancelado && !isOrderTypeFullGift && (
                              <PermissionGuard permission="invoices.create">
                                <button
                                  className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                  title="Emitir Nota Fiscal de Venda"
                                  onClick={async () => {
                                    try {
                                      const fullOrder = await salesOrderService.getOrderById(order.order_id);
                                      navigate('/invoices/new', { state: { orderData: fullOrder, invoiceMode: 'sales' } });
                                    } catch {
                                      setError('Erro ao carregar dados do pedido para emissão de NF');
                                    }
                                  }}
                                >
                                  <Send size={16} />
                                </button>
                              </PermissionGuard>
                            )}
                            {!order.gift_invoice_id && (order.has_gift_items || isOrderTypeFullGift) && (
                              [OrderStatusId.Conferido, OrderStatusId.Atendido].includes(order.order_status_id ?? 0)
                              || (!!order.invoice_id && !order.gift_invoice_id)
                            ) && order.order_status_id !== OrderStatusId.Cancelado && (
                              <PermissionGuard permission="invoices.create">
                                <button
                                  className="p-1.5 text-pink-500 hover:text-pink-700 hover:bg-pink-50 rounded transition-colors"
                                  title="Emitir NF de Bonificação (presente)"
                                  onClick={async () => {
                                    try {
                                      const fullOrder = await salesOrderService.getOrderById(order.order_id);
                                      navigate('/invoices/new', { state: { orderData: fullOrder, invoiceMode: 'gift', forceFullGiftOrder: isOrderTypeFullGift } });
                                    } catch {
                                      setError('Erro ao carregar dados do pedido para emissão de NF de bonificação');
                                    }
                                  }}
                                >
                                  <Send size={16} />
                                </button>
                              </PermissionGuard>
                            )}
                            {!isOrderStatusBlocked(order.order_status_id) && (
                              <PermissionGuard permission="orders.split">
                                <button
                                  className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                                  title="Desmembrar pedido"
                                  onClick={() => handleOpenSplit(order.order_id)}
                                >
                                  <Scissors size={16} />
                                </button>
                              </PermissionGuard>
                            )}
                            <PermissionGuard permission="orders.read">
                              <button
                                className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                                title="Imprimir Etiqueta de Envio"
                                onClick={async () => {
                                  try {
                                    await salesOrderService.downloadShippingLabel(order.order_id);
                                  } catch (err) {
                                    console.error('Erro ao gerar etiqueta:', err);
                                  }
                                }}
                              >
                                <Tag size={16} />
                              </button>
                            </PermissionGuard>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* Expanded items — fetched on demand via getOrderById */}
                      {expandedOrderId === order.order_id && (
                        <tr>
                          <td colSpan={15} className="p-0 border-b border-slate-200">
                            {orderItemsCache[order.order_id]?.loading ? (
                              <div className="flex items-center gap-2 px-6 py-4 text-sm text-slate-500 bg-slate-50">
                                <Loader2 size={16} className="animate-spin text-emerald-600" />
                                Carregando itens...
                              </div>
                            ) : (
                              <div className="overflow-x-auto bg-emerald-50/20">
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="bg-slate-100 border-b border-slate-200">
                                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">SKU</th>
                                      <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Descrição</th>
                                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Qtd</th>
                                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Est. Físico</th>
                                      <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap" title="Estoque disponível para venda, descontando reservas de pedidos pendentes">Est. Disponível</th>
                                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">Valor Unit.</th>
                                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Desconto (%)</th>
                                      <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(orderItemsCache[order.order_id]?.items ?? []).length > 0 ? (
                                      (orderItemsCache[order.order_id]?.items ?? []).map(item => {
                                        const sd = stockCache[item.sku];
                                        const sqty = sd?.stock_quantity;
                                        const savail = sd?.stock_quantity_available;
                                        const hasDiff = sqty != null && savail != null && sqty !== savail;
                                        const availColor = savail == null ? 'text-slate-400' : savail < 0 ? 'text-red-600' : savail === 0 ? 'text-amber-600' : (sd?.min_stock_quantity != null && savail < sd.min_stock_quantity) ? 'text-orange-600' : 'text-emerald-600';
                                        return (
                                        <tr key={item.order_item_id} className="bg-white hover:bg-emerald-50/60 border-b border-slate-100 transition-colors">
                                          <td className="px-4 py-2 font-mono font-semibold text-slate-800 text-xs whitespace-nowrap">{item.sku}</td>
                                          <td className="px-4 py-2 text-slate-700 text-xs">{item.description || '—'}</td>
                                          <td className="px-4 py-2 text-center text-xs text-slate-700">{item.quantity}</td>
                                          <td className="px-4 py-2 text-center text-xs text-slate-700">{sqty != null ? sqty : '—'}</td>
                                          <td className="px-4 py-2 text-center text-xs whitespace-nowrap">
                                            {savail != null ? (
                                              <span className={`font-medium ${availColor} inline-flex items-center gap-1`}>
                                                {savail < 0 && <AlertTriangle size={12} />}
                                                {savail}
                                                {hasDiff && sd?.product_id && (
                                                  <button
                                                    className="ml-1 text-emerald-600 hover:text-emerald-800 underline text-[10px] font-normal"
                                                    title="Ver pedidos pendentes que reservam estoque"
                                                    onClick={() => setPendingModal({ open: true, productId: sd.product_id, sku: item.sku, description: item.description })}
                                                  >ver reservas</button>
                                                )}
                                              </span>
                                            ) : '—'}
                                          </td>
                                          <td className="px-4 py-2 text-right text-xs text-slate-700 whitespace-nowrap">{formatCurrency(item.unit_price)}</td>
                                          <td className="px-4 py-2 text-right text-xs text-amber-600 whitespace-nowrap">
                                            {item.discount ? `${item.discount}%` : '—'}
                                          </td>
                                          <td className="px-4 py-2 text-right text-xs font-semibold text-emerald-700 whitespace-nowrap">
                                            {formatCurrency((item.unit_price || 0) * (1 - (item.discount || 0) / 100) * item.quantity)}
                                          </td>
                                        </tr>
                                        );
                                      })
                                    ) : (
                                      <tr>
                                        <td colSpan={8} className="px-4 py-4 text-center text-slate-400 text-sm italic">
                                          Nenhum item neste pedido
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
          })()}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-1">
        <p className="text-sm text-slate-500 order-2 sm:order-1">
          {totalOrders > 0
            ? `${((currentPage - 1) * pageSize) + 1}–${Math.min(currentPage * pageSize, totalOrders)} de ${totalOrders} registro${totalOrders !== 1 ? 's' : ''} · Página ${currentPage} de ${totalPages}`
            : 'Nenhum registro'}
        </p>
        {totalPages > 1 && (
          <div className="flex items-center gap-1 order-1 sm:order-2">
            {/* First */}
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(1)}
              title="Primeira página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >«</button>
            {/* Previous */}
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
              title="Página anterior"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >‹</button>
            {/* Page numbers */}
            {getPageRange(currentPage, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ell-${i}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-sm">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(Number(p))}
                  title={`Página ${p}`}
                  className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${
                    currentPage === p
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >{p}</button>
              )
            )}
            {/* Next */}
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              title="Próxima página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >›</button>
            {/* Last */}
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              title="Última página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >»</button>
          </div>
        )}
      </div>

      {/* Clone confirm */}
      <ConfirmDialog
        isOpen={cloneOrderId !== null}
        onClose={() => setCloneOrderId(null)}
        onConfirm={handleClone}
        title="Clonar Pedido"
        message={`Deseja clonar o pedido #${cloneOrderId}? Um novo pedido será criado com os mesmos dados e status "Em Aberto".`}
        confirmText="Clonar"
        cancelText="Cancelar"
        severity="info"
        loading={cloning}
      />

      {/* Status change confirm */}
      <ConfirmDialog
        isOpen={statusChange !== null}
        onClose={() => setStatusChange(null)}
        onConfirm={confirmStatusChange}
        title="Alterar Status"
        message={`Deseja alterar o status do pedido #${statusChange?.orderId} para "${statusChange ? (getStatusOptionById(statusChange.statusId)?.label ?? statusChange.statusId) : ''}"?`}
        confirmText="Alterar"
        cancelText="Cancelar"
        severity="warning"
        loading={changingStatus}
      />

      {/* Split modal */}
      {splitOrder && (
        <SplitOrderModal
          isOpen={true}
          onClose={() => setSplitOrder(null)}
          onConfirm={handleSplitConfirm}
          order={splitOrder}
          loading={splitting}
          statusOptions={manualStatusOpts}
        />
      )}

      {/* Pending orders modal */}
      <PendingOrdersModal
        isOpen={pendingModal.open}
        onClose={() => setPendingModal({ open: false, productId: null, sku: '' })}
        productId={pendingModal.productId}
        productSku={pendingModal.sku}
        productDescription={pendingModal.description}
      />
    </div>
  );
};
